import { useCallback, useEffect } from 'react';
import { useAuth } from '../../auth/AuthContext';
import { listCurrencies, saveCurrencyPreference } from '../../api/currencies';
import Icon from '../components/Icons';
import { BASE_CURRENCY, selectCurrency, setCurrencies, useCurrencyState } from './currencyStore';

/**
 * Loads the offered currencies and applies the signed-in account's saved currency.
 * Call it from the component that renders the page tree: it re-renders on a currency change,
 * so every price on the page is shown again in the new currency without losing form state.
 */
export function useCurrencySync() {
  const { user } = useAuth();
  const state = useCurrencyState();

  useEffect(() => {
    const controller = new AbortController();
    listCurrencies(controller.signal).then(setCurrencies).catch(() => { /* INR only until the API is reachable */ });
    return () => controller.abort();
  }, []);

  // The account's saved choice wins when someone signs in.
  const preferred = user?.preferredCurrency;
  useEffect(() => {
    if (preferred) selectCurrency(preferred);
  }, [preferred, user?.id]);

  return state;
}

/** Selects a currency and, when signed in, saves it on the account. */
export function useChooseCurrency() {
  const { auth, signInWith } = useAuth();
  return useCallback(async (code) => {
    selectCurrency(code);
    if (!auth?.accessToken) return;
    try {
      const user = await saveCurrencyPreference(auth.accessToken, code);
      signInWith({ ...auth, user: { ...auth.user, preferredCurrency: user.preferredCurrency } });
    } catch { /* kept for this browser; the account keeps its previous choice */ }
  }, [auth, signInWith]);
}

export default function CurrencySelect() {
  const { currencies, code } = useCurrencyState();
  const choose = useChooseCurrency();
  const offered = currencies.some((c) => c.code === code) ? code : BASE_CURRENCY;
  if (currencies.length < 2) {
    return <span className="topbar-sel"><Icon name="globe" size={14} /> IN | ENG | {BASE_CURRENCY}</span>;
  }
  return (
    <label className="topbar-sel currency-select">
      <Icon name="globe" size={14} /> IN | ENG |
      <select aria-label="Currency" value={offered} onChange={(e) => choose(e.target.value)}>
        {currencies.map((c) => <option key={c.code} value={c.code} title={c.name}>{c.code}</option>)}
      </select>
    </label>
  );
}
